import { acceptTerms } from '../../api/rpc';
import { toRpcError } from '../../api/errors';
import { session } from '../../core/session';
import { TERMS_MD } from './terms-text';
import { button, createScreen, createSheet, h } from './common';

export type TermsBlock =
  | { t: 'h1' | 'h2' | 'p'; text: string }
  | { t: 'ol'; items: string[] }
  | { t: 'table'; rows: string[][] };

/** terms.md の範囲だけを読む小さな変換（見出し・段落・番号リスト・表） */
export function parseTerms(md: string): TermsBlock[] {
  const out: TermsBlock[] = [];
  for (const raw of md.split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) continue;
    const last = out[out.length - 1];
    if (line.startsWith('## ')) out.push({ t: 'h2', text: line.slice(3) });
    else if (line.startsWith('# ')) out.push({ t: 'h1', text: line.slice(2) });
    else if (/^\d+\.\s/.test(line)) {
      const item = line.replace(/^\d+\.\s/, '');
      if (last?.t === 'ol') last.items.push(item);
      else out.push({ t: 'ol', items: [item] });
    } else if (line.startsWith('|')) {
      const cells = line.split('|').slice(1, -1).map((c) => c.trim());
      if (cells.every((c) => /^-+$/.test(c))) continue;
      if (last?.t === 'table') last.rows.push(cells);
      else out.push({ t: 'table', rows: [cells] });
    } else out.push({ t: 'p', text: line });
  }
  return out;
}

function render(blocks: TermsBlock[]): HTMLElement {
  const box = h('div');
  for (const b of blocks) {
    if (b.t === 'h1') {
      const e = h('h2', undefined, b.text);
      e.style.cssText = 'margin:0 0 8px;font-size:20px;font-weight:900';
      box.appendChild(e);
    } else if (b.t === 'h2') {
      const e = h('h3', undefined, b.text);
      e.style.cssText = 'margin:18px 0 6px;font-size:16px;font-weight:900';
      box.appendChild(e);
    } else if (b.t === 'p') {
      const e = h('p', undefined, b.text);
      e.style.cssText = 'font-size:14px;line-height:1.75;margin:0 0 8px';
      box.appendChild(e);
    } else if (b.t === 'ol') {
      const ol = h('ol');
      ol.style.cssText = 'margin:0 0 8px;padding-left:1.4em;font-size:14px;line-height:1.75';
      for (const it of b.items) ol.appendChild(h('li', undefined, it));
      box.appendChild(ol);
    } else if (b.t === 'table') {
      const tbl = h('table');
      tbl.style.cssText = 'border-collapse:collapse;width:100%;font-size:13px;line-height:1.6;margin:0 0 8px';
      b.rows.forEach((r, i) => {
        const tr = h('tr');
        for (const c of r) {
          const cell = h(i === 0 ? 'th' : 'td', undefined, c);
          cell.style.cssText = 'border:1px solid var(--edge);padding:4px 6px;text-align:left;vertical-align:top';
          tr.appendChild(cell);
        }
        tbl.appendChild(tr);
      });
      box.appendChild(tbl);
    }
  }
  return box;
}

/**
 * 大学のロゴ2つと研究室のロゴ（許可済み、本人11/4）。public/assets/logos。無いものは出さない。
 * 校章と文字のロゴは1つの組にし、研究室のロゴは細い線で区切って右に置く。
 * 白い地は multiply で紙の色になじませる（ロゴの色と比率は変えない）
 */
const LOGOS = [
  { file: 'logo1.jpg', alt: '東京理科大学 校章', height: 48 },
  { file: 'logo2.jpg', alt: '東京理科大学', height: 26 },
  { file: 'logo3.png', alt: '石垣研究室', height: 36, divider: true },
];

function logos(): HTMLElement {
  const row = h('div', 'sc-logos');
  row.style.cssText = 'display:flex;justify-content:center;align-items:center;gap:12px;margin:0 0 12px';
  let left = LOGOS.length;
  for (const l of LOGOS) {
    let line: HTMLElement | null = null;
    if ('divider' in l && l.divider) {
      line = h('span');
      line.setAttribute('aria-hidden', 'true');
      line.style.cssText = 'width:1px;height:34px;background:var(--edge)';
      row.appendChild(line);
    }
    const img = h('img');
    img.src = `/assets/logos/${l.file}`;
    img.alt = l.alt;
    img.style.cssText = `height:${l.height}px;width:auto;max-width:70%;object-fit:contain;mix-blend-mode:multiply`;
    img.addEventListener('error', () => {
      img.remove();
      line?.remove();
      if (--left === 0) row.remove();
    });
    row.appendChild(img);
  }
  return row;
}

/**
 * 規約の表示と同意。端末の情報（User-Agent・メール等）は送らない。
 * 広報への利用は規約への同意に含める（本人11/3）ので選択の欄はなく、常に true を送る。ニックネームの欄もやめた。
 * 推測：見た目は未確定。ログイン画面と同じ、机の上の紙の最小の形。
 */
export async function showTerms(root: HTMLElement, termsVersion: string): Promise<void> {
  const screen = createScreen(root);
  const { wrap, sheet } = createSheet(809);
  screen.appendChild(wrap);
  sheet.appendChild(logos());
  sheet.appendChild(h('h1', undefined, '参加規約'));

  const scroll = h('div');
  scroll.style.cssText = 'max-height:42dvh;overflow-y:auto;margin:0 0 14px;padding:10px 12px;background:var(--tag);border:1px solid var(--edge)';
  scroll.tabIndex = 0;
  scroll.appendChild(render(parseTerms(TERMS_MD)));
  sheet.appendChild(scroll);

  const err = h('p', 'sh-err');
  err.setAttribute('role', 'alert');
  sheet.appendChild(err);

  return new Promise<void>((resolve) => {
    let busy = false;
    const go = button('同意して始める', 'ink', () => {
      if (busy) return;
      busy = true;
      go.setAttribute('aria-disabled', 'true');
      err.textContent = '';
      acceptTerms({ termsVersion, publicityOptIn: true, nickname: null, userAgent: null })
        .then(() => {
          session.markTermsAccepted();
          screen.remove();
          resolve();
        })
        .catch((e: unknown) => {
          const code = toRpcError(e).code;
          err.textContent = code === 'invalid_session' ? 'もう一度入り直してください' : '通信できませんでした';
          busy = false;
          go.setAttribute('aria-disabled', 'false');
        });
    });
    screen.appendChild(go);
  });
}
